// The import / export dialog (spec/dashboard.md section 9.6; spec/api-conventions.md section 13.4), addressed by
// `?dialog=import | export` from the strings toolbar (layout "localization") and the documents grid (layout "template").
// Import: a CSV (pasted or a chosen file), JSON rows or a Google Sheet; Dry run first (the report per table and row),
// then Apply runs the same request for real (one commit). Export: CSV / JSON downloads, or a Google Sheet's tabs written.
// The Sheets access token is for one request only: never stored, never logged; the spreadsheet id and tab are remembered
// per user in localStorage["hue.sheets:<game id>:<layout>:<template | group>"]. When the organization has a Google service
// account (M7 slice 3: `GET /v1/orgs/{org}/integrations`, read once per dialog), the token field goes: the request names no
// accessToken and the dialog says which address the sheet is shared with.
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { ErrorDetail } from '../../api/types';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge, type Tone } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { AlertCircle, CheckCircle2, Copy } from '../../ui/icons';
import { Checkbox, Select, TextArea, TextInput } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { useWorkspace } from '../../workspace/react';
import { useContent } from '../ContentContext';
import { downloadText } from '../localization/strings';

type ImportResult = components['schemas']['ImportResultDto'];
type ExportResult = components['schemas']['ExportResultDto'];
type SourceKind = 'csv' | 'json' | 'google_sheets';
export type ImportLayout = 'localization' | 'template';

export const GCLOUD_HINT = 'gcloud auth print-access-token --scopes=https://www.googleapis.com/auth/drive';

export function sheetsStorageKey(gameId: string, layout: ImportLayout, scope: string): string {
  return `hue.sheets:${gameId}:${layout}:${scope}`;
}

function loadSheet(key: string): { spreadsheetId: string; tab: string } {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const v = JSON.parse(raw) as { spreadsheetId?: string; tab?: string };
      return { spreadsheetId: v.spreadsheetId ?? '', tab: v.tab ?? '' };
    }
  } catch {
    // no storage: nothing remembered
  }
  return { spreadsheetId: '', tab: '' };
}

function saveSheet(key: string, value: { spreadsheetId: string; tab: string }): void {
  try {
    if (value.spreadsheetId || value.tab) localStorage.setItem(key, JSON.stringify(value)); // never the token
  } catch {
    // ignore
  }
}

/** Hosts the dialog: renders when the URL carries `?dialog=import` or `?dialog=export`. */
export function ImportExportDialog({ layout, template, group }: { layout: ImportLayout; template?: string | null; group?: string | null }) {
  const dialog = useUrlParam('dialog');
  if (dialog.value !== 'import' && dialog.value !== 'export') return null;
  return dialog.value === 'import' ? <ImportDialog layout={layout} template={template ?? null} group={group ?? null} onClose={dialog.close} /> : <ExportDialog layout={layout} template={template ?? null} group={group ?? null} onClose={dialog.close} />;
}

/** The scope pickers both dialogs share: the group (localization) or the template (template layout), and the Sheets fields. */
function useScope(layout: ImportLayout, template: string | null, group: string | null) {
  const catalog = useContent();
  const { game } = useGame();
  const [scopeGroup, setScopeGroup] = useState(group ?? catalog.groups[0] ?? 'DEFAULT');
  const [scopeTemplate, setScopeTemplate] = useState(template ?? '');
  const scope = layout === 'localization' ? scopeGroup : scopeTemplate;
  const storageKey = sheetsStorageKey(game.id, layout, scope);
  const [sheet, setSheet] = useState(() => loadSheet(storageKey));
  useEffect(() => setSheet(loadSheet(storageKey)), [storageKey]);
  const [token, setToken] = useState('');
  // the organization's Google service account, when configured (a caller without org_manage reads a 403: the token field stays)
  const { org } = useGame();
  const { client } = useAuth();
  const [account, setAccount] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    client.data(ops.getIntegrations, { path: { org } }).then(
      (r) => {
        if (!cancelled && r?.googleSheets?.configured) setAccount(r.googleSheets.clientEmail ?? '');
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [client, org]);
  /** the token the request carries: none when the service account signs it */
  const tokenField = account === null ? { accessToken: token } : {};
  const hasCredential = account !== null || !!token;
  return { catalog, scopeGroup, setScopeGroup, scopeTemplate, setScopeTemplate, scope, storageKey, sheet, setSheet, token, setToken, account, tokenField, hasCredential };
}

function ScopeFields({ layout, s, groupOptional }: { layout: ImportLayout; s: ReturnType<typeof useScope>; groupOptional?: boolean }) {
  const listId = 'import-groups';
  if (layout === 'localization') {
    return (
      <>
        <Field label="Group" required={!groupOptional} help={groupOptional ? 'Optional for Google Sheets: without a tab, every tab named like a group is imported.' : 'The group the rows belong to (Key is the bare key).'}>
          <TextInput value={s.scopeGroup} onChange={(e) => s.setScopeGroup(e.target.value)} list={listId} aria-label="Group" />
        </Field>
        <datalist id={listId}>
          {s.catalog.groups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
      </>
    );
  }
  const templates = s.catalog.templateList.filter((t) => t.kind === 'document' || t.kind === 'singleton' || t.kind === 'component');
  return (
    <Field label="Template" required>
      <Select value={s.scopeTemplate} onChange={(e) => s.setScopeTemplate(e.target.value)} aria-label="Template">
        {!s.scopeTemplate && <option value="">Choose a template…</option>}
        {templates.map((t) => (
          <option key={t.name} value={t.name}>
            {t.displayName ?? t.name} ({t.name})
          </option>
        ))}
      </Select>
    </Field>
  );
}

function SheetFields({ s, tabRequired }: { s: ReturnType<typeof useScope>; tabRequired: boolean }) {
  return (
    <div className="form-grid">
      <Field label="Spreadsheet id" required help="Remembered in this browser for this group / template.">
        <TextInput value={s.sheet.spreadsheetId} onChange={(e) => s.setSheet({ ...s.sheet, spreadsheetId: e.target.value.trim() })} aria-label="Spreadsheet id" placeholder="1AbC…" />
      </Field>
      <Field label="Tab" required={tabRequired} help={tabRequired ? undefined : 'Optional: without one, every tab named like a group with a Key column.'}>
        <TextInput value={s.sheet.tab} onChange={(e) => s.setSheet({ ...s.sheet, tab: e.target.value })} aria-label="Tab" />
      </Field>
      {s.account !== null ? (
        <p className="small span-2" role="note" data-testid="sheets-account">
          The organization's service account signs this request: the sheet is shared with <code>{s.account}</code>{' '}
          <Button size="s" variant="ghost" icon={<Copy aria-hidden="true" />} aria-label="Copy the service account address" onClick={() => void navigator.clipboard?.writeText(s.account ?? '').catch(() => undefined)}>
            Copy
          </Button>
        </p>
      ) : (
        <Field
          className="span-2"
          label="Access token"
          required
          help={
            <>
              For this request only: never stored, never logged. Get one with <code>{GCLOUD_HINT}</code>
            </>
          }
        >
          <TextInput type="password" autoComplete="off" value={s.token} onChange={(e) => s.setToken(e.target.value.trim())} aria-label="Access token" />
        </Field>
      )}
    </div>
  );
}

function SourceKindPicker({ value, onChange, label }: { value: SourceKind; onChange: (k: SourceKind) => void; label: string }) {
  return (
    <div className="field">
      <span className="field-label" aria-hidden="true">
        {label}
      </span>
      <div className="row wrap" role="radiogroup" aria-label={label}>
      {(
        [
          ['csv', 'CSV'],
          ['json', 'JSON'],
          ['google_sheets', 'Google Sheets'],
        ] as const
      ).map(([k, text]) => (
        <label key={k} className="check">
          <input type="radio" name={label} value={k} checked={value === k} onChange={() => onChange(k)} />
          <span>{text}</span>
        </label>
      ))}
      </div>
    </div>
  );
}

const statusTone: Record<string, Tone> = { added: 'success', changed: 'warning', unchanged: 'neutral', removed: 'danger', failed: 'danger' };

function ImportDialog({ layout, template, group, onClose }: { layout: ImportLayout; template: string | null; group: string | null; onClose: () => void }) {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const workspace = useWorkspace();
  const toasts = useToasts();
  const s = useScope(layout, template, group);
  const canWrite = can('content_write') && !workspace.isProtected;
  const [kind, setKind] = useState<SourceKind>('csv');
  const [csv, setCsv] = useState('');
  const [json, setJson] = useState('');
  const [matchBy, setMatchBy] = useState('');
  const [removeMissing, setRemoveMissing] = useState(false);
  const [clearEmpty, setClearEmpty] = useState(false);
  const [busy, setBusy] = useState<null | 'dry' | 'apply'>(null);
  const [report, setReport] = useState<{ body: string; result: ImportResult } | null>(null);
  const [failure, setFailure] = useState<{ message: string; details: ErrorDetail[] } | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const params = layout === 'template' ? (s.catalog.templates.get(s.scopeTemplate)?.effectiveParameters ?? []) : [];

  const compose = (): { body: Record<string, unknown> | null; error: string | null } => {
    let rows: unknown = null;
    if (kind === 'json') {
      try {
        rows = JSON.parse(json || '[]');
        if (!Array.isArray(rows)) return { body: null, error: 'The JSON must be an array of row objects.' };
      } catch (e) {
        return { body: null, error: e instanceof Error ? e.message : String(e) };
      }
    }
    const sheets = kind === 'google_sheets';
    const body = {
      layout,
      template: layout === 'template' ? s.scopeTemplate || null : null,
      group: layout === 'localization' ? s.scopeGroup || null : null,
      source: {
        kind,
        text: kind === 'csv' ? csv : null,
        rows: kind === 'json' ? rows : null,
        spreadsheetId: sheets ? s.sheet.spreadsheetId : null,
        tab: sheets ? s.sheet.tab || null : null,
        ...(sheets ? s.tokenField : { accessToken: null }),
      },
      matchBy: layout === 'template' && matchBy ? matchBy : null,
      removeMissing,
      clearEmpty,
      message: null,
    };
    return { body, error: null };
  };
  // what the dry run was for, without the token: Apply needs a dry run of the current form
  const fingerprint = (body: Record<string, unknown> | null) => (body ? JSON.stringify({ ...body, source: { ...(body.source as object), accessToken: null } }) : '');
  const ready =
    (layout === 'localization' ? kind === 'google_sheets' || !!s.scopeGroup : !!s.scopeTemplate) &&
    (kind === 'csv' ? csv.trim().length > 0 : kind === 'json' ? json.trim().length > 0 : !!s.sheet.spreadsheetId && s.hasCredential && (layout === 'localization' || !!s.sheet.tab));

  const run = async (dryRun: boolean) => {
    const { body, error } = compose();
    setJsonError(error);
    if (!body) return;
    setBusy(dryRun ? 'dry' : 'apply');
    setFailure(null);
    if (kind === 'google_sheets') saveSheet(s.storageKey, s.sheet);
    try {
      const r = await client.call(ops.importTable, { path: { org, game: game.slug, branch }, body: { ...body, dryRun } as never });
      setReport({ body: fingerprint(body), result: r.data });
      if (!dryRun) {
        const commit = r.data.commit || r.commit;
        if (r.commit) workspace.setHead(r.commit);
        toasts.success(r.data.applied ? `Imported in commit ${String(commit).slice(0, 8)}.` : 'Imported: nothing changed, no commit.');
        s.catalog.refresh();
      }
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      setFailure({ message: err.message, details: err.details ?? [] });
      if (!dryRun) setReport(null);
    } finally {
      setBusy(null);
    }
  };

  const result = report?.result;
  const current = useMemo(() => (ready ? fingerprint(compose().body) : ''), [ready, kind, csv, json, matchBy, removeMissing, clearEmpty, s.scopeGroup, s.scopeTemplate, s.sheet]); // eslint-disable-line react-hooks/exhaustive-deps
  const stale = !!report && report.body !== current;
  const anyFailed = !!result && result.tables.some((t) => Number(t.failed) > 0);
  const applied = !!result && !result.dryRun;

  return (
    <Modal
      title={layout === 'localization' ? 'Import localization' : `Import ${s.scopeTemplate || 'documents'}`}
      open
      size="l"
      onClose={onClose}
      footer={
        <>
          <span className="muted small">{applied ? 'Applied.' : result ? (stale ? 'The form changed: dry-run again before applying.' : 'Dry run done: review the rows, then Apply (one commit).') : 'Dry run first: nothing is written.'}</span>
          <span className="spacer" />
          <Button onClick={onClose}>{applied ? 'Close' : 'Cancel'}</Button>
          {!applied && (
            <>
              <Button variant={result && !stale ? 'secondary' : 'primary'} busy={busy === 'dry'} disabled={!ready || busy !== null} missingPermission={can('content_write') ? null : 'content_write'} onClick={() => void run(true)}>
                Dry run
              </Button>
              <Button variant="primary" busy={busy === 'apply'} disabled={!canWrite || !result || stale || anyFailed || busy !== null} onClick={() => void run(false)} title={anyFailed ? 'Rows failed: fix the source and dry-run again.' : undefined}>
                Apply
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="stack" data-testid="import-dialog">
        <div className="form-grid">
          <ScopeFields layout={layout} s={s} groupOptional={kind === 'google_sheets'} />
          <SourceKindPicker value={kind} onChange={setKind} label="Source" />
        </div>
        {kind === 'csv' && (
          <>
            <Field label="CSV" required help={layout === 'localization' ? 'Columns: Key and one per language headed like "English [en]".' : 'Columns: Id, BalancyId, BalancyExportable and one per parameter.'}>
              <TextArea value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} aria-label="CSV text" spellCheck={false} />
            </Field>
            <label className="row small muted">
              <span>Or choose a file:</span>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                aria-label="CSV file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void file.text().then(setCsv);
                }}
              />
            </label>
          </>
        )}
        {kind === 'json' && (
          <Field label="JSON rows" required help='An array of objects keyed by header, e.g. [{"Key":"HELLO","English [en]":"Hello"}].' error={jsonError}>
            <TextArea value={json} onChange={(e) => setJson(e.target.value)} rows={8} aria-label="JSON rows" spellCheck={false} />
          </Field>
        )}
        {kind === 'google_sheets' && <SheetFields s={s} tabRequired={layout === 'template'} />}
        <div className="form-grid">
          {layout === 'template' && (
            <Field label="Match by" help="After Id and BalancyId, rows match an existing document by this parameter.">
              <Select value={matchBy} onChange={(e) => setMatchBy(e.target.value)} aria-label="Match by">
                <option value="">Id, then BalancyId</option>
                {params.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.displayName ?? p.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <div className="field span-2 stack">
            <Checkbox label={layout === 'localization' ? 'Remove missing (delete the group’s strings no row names)' : 'Remove missing (delete the documents no row matched)'} checked={removeMissing} onChange={(e) => setRemoveMissing(e.target.checked)} />
            <Checkbox label="Clear empty (an empty cell clears the value)" checked={clearEmpty} onChange={(e) => setClearEmpty(e.target.checked)} />
          </div>
        </div>
        {failure && <FailureList failure={failure} />}
        {result && <ImportReport result={result} />}
      </div>
    </Modal>
  );
}

function FailureList({ failure }: { failure: { message: string; details: ErrorDetail[] } }) {
  return (
    <div className="banner banner-danger" role="alert">
      <AlertCircle className="banner-icon" aria-hidden="true" />
      <div className="banner-body stack">
      <span>{failure.message}</span>
      {failure.details.length > 0 && (
        <div className="grid-wrap">
        <table className="grid" data-testid="import-failures">
          <thead>
            <tr>
              <th scope="col">Table</th>
              <th scope="col">Row</th>
              <th scope="col">Entity</th>
              <th scope="col">Error</th>
            </tr>
          </thead>
          <tbody>
            {failure.details.map((d, i) => (
              <tr key={i} className="grid-row">
                <td>{d.field ?? ''}</td>
                <td>{d.index ?? ''}</td>
                <td>
                  <code>{d.entityId ?? ''}</code>
                </td>
                <td>{d.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      </div>
    </div>
  );
}

function ImportReport({ result }: { result: ImportResult }) {
  return (
    <div className="stack" data-testid="import-report">
      <p className="small">
        {result.dryRun ? <Badge tone="info">dry run</Badge> : <Badge tone="success">applied</Badge>} {result.commit ? <code>{result.commit.slice(0, 8)}</code> : null}
      </p>
      {result.tables.map((t, i) => (
        <section key={i} className="stack">
          <div className="section-head">
            <h3 className="section-title">
              {t.template ?? t.group ?? t.layout}
              {t.tab ? <span className="muted"> · tab {t.tab}</span> : null}
            </h3>
          </div>
          <p className="small muted">
            added {String(t.added)} · changed {String(t.changed)} · unchanged {String(t.unchanged)} · removed {String(t.removed)} · <span className={Number(t.failed) > 0 ? 'error' : ''}>failed {String(t.failed)}</span>
          </p>
          {t.warnings.length > 0 && (
            <ul className="warning small">
              {t.warnings.map((w, j) => (
                <li key={j}>{w}</li>
              ))}
            </ul>
          )}
          {t.rows.length > 0 && (
            <div className="grid-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">Row</th>
                  <th scope="col">Key / id</th>
                  <th scope="col">Status</th>
                  <th scope="col">Error</th>
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, j) => (
                  <tr key={j} className={`grid-row ${r.status === 'failed' ? 'invalid' : ''}`}>
                    <td>{String(r.row)}</td>
                    <td>
                      <code>{r.key ?? r.id ?? ''}</code>
                    </td>
                    <td>
                      <Badge tone={statusTone[r.status] ?? 'neutral'}>{r.status}</Badge>
                    </td>
                    <td className="small">{r.error ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>
      ))}
      {result.skippedTabs.length > 0 && <p className="muted small">Skipped tabs: {result.skippedTabs.join(', ')}</p>}
    </div>
  );
}

function ExportDialog({ layout, template, group, onClose }: { layout: ImportLayout; template: string | null; group: string | null; onClose: () => void }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const toasts = useToasts();
  const s = useScope(layout, template, group);
  const [kind, setKind] = useState<SourceKind>('csv');
  const [exportedOnly, setExportedOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [failure, setFailure] = useState<{ message: string; details: ErrorDetail[] } | null>(null);
  const sheets = kind === 'google_sheets';
  const ready = (layout === 'localization' ? !!s.scopeGroup : !!s.scopeTemplate) && (!sheets || (!!s.sheet.spreadsheetId && s.hasCredential));
  const name = (layout === 'localization' ? s.scopeGroup : s.scopeTemplate) || 'export';

  const run = async () => {
    setBusy(true);
    setFailure(null);
    setResult(null);
    if (sheets) saveSheet(s.storageKey, s.sheet);
    try {
      const r = await client.data(ops.exportTable, {
        path: { org, game: game.slug, branch },
        body: {
          layout,
          template: layout === 'template' ? s.scopeTemplate : null,
          group: layout === 'localization' ? s.scopeGroup : null,
          exportedOnly,
          // the generated target type marks accessToken required; the API takes it as optional (absent: the service account)
          target: { kind, spreadsheetId: sheets ? s.sheet.spreadsheetId : null, tab: sheets ? s.sheet.tab || null : null, ...(sheets ? s.tokenField : { accessToken: null }) } as { kind: string; spreadsheetId: string | null; tab: string | null; accessToken: string | null },
        },
      });
      setResult(r);
      if (kind === 'csv') downloadText(`${name}.csv`, r.csv ?? '');
      else if (kind === 'json') downloadText(`${name}.json`, JSON.stringify(r.data ?? [], null, 2), 'application/json');
      toasts.success(sheets ? `Wrote ${String(r.rows)} rows to ${(r.tabs ?? []).join(', ') || 'the sheet'}.` : `Exported ${String(r.rows)} rows.`);
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      setFailure({ message: err.message, details: err.details ?? [] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={layout === 'localization' ? 'Export localization' : `Export ${s.scopeTemplate || 'documents'}`}
      open
      size="m"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          <Button variant="primary" busy={busy} disabled={!ready} onClick={() => void run()}>
            Export
          </Button>
        </>
      }
    >
      <div className="stack" data-testid="export-dialog">
        <p className="small">
          Layout: <strong>{layout}</strong>
        </p>
        <ScopeFields layout={layout} s={s} />
        <Checkbox label={layout === 'localization' ? 'Exported strings only' : 'Exported documents only'} checked={exportedOnly} onChange={(e) => setExportedOnly(e.target.checked)} />
        <SourceKindPicker value={kind} onChange={setKind} label="Target" />
        {kind === 'csv' && <p className="muted small">Downloads {name}.csv.</p>}
        {kind === 'json' && <p className="muted small">Downloads {name}.json.</p>}
        {sheets && <SheetFields s={s} tabRequired={false} />}
        {failure && <FailureList failure={failure} />}
        {result && (
          <div className="banner banner-success small" data-testid="export-result">
            <CheckCircle2 className="banner-icon" aria-hidden="true" />
            {sheets ? (
              <p>
                Wrote {String(result.rows)} rows to {result.spreadsheetId ? <code>{result.spreadsheetId}</code> : 'the spreadsheet'}: tabs {(result.tabs ?? []).join(', ') || '—'}.
              </p>
            ) : (
              <p>
                {String(result.rows)} rows exported to {name}.{kind === 'csv' ? 'csv' : 'json'}.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
